import { Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { DateTime } from "luxon";
import { ReactElement } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikDateTimePicker from "~frontend/components/forms/FormikDateTimePicker";
import {
  timeToHHMM,
  timeFromHHMM,
} from "~frontend/components/forms/FormikTextingHoursFields";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";

import { CampaignForView } from "../types";

export type RoiFormValues = {
  roiTestMinimumRoiPct: string;
  roiTestMinimumDonationsPerVariant: string;
  roiTestExcludeDonationsOver: string;
  roiTestExpiresAt: DateTime | null;
  roiTestHoursStart: string;
  roiTestHoursEnd: string;
};

export function roiFormValuesFromCampaign(
  campaign: CampaignForView,
  subaccount: SubaccountAdminData
): RoiFormValues {
  if (!campaign.roiTestConfig) {
    throw new Error("Campaign does not have an ROI test config");
  }

  return {
    roiTestMinimumRoiPct: Math.round(
      campaign.roiTestConfig.minimumRoi * 100
    ).toString(),
    roiTestMinimumDonationsPerVariant:
      campaign.roiTestConfig.minimumDonationsPerVariant?.toString() ?? "",
    roiTestExcludeDonationsOver: campaign.roiTestConfig
      .excludeDonationsOverCents
      ? (
          campaign.roiTestConfig.excludeDonationsOverCents / 100.0
        ).toLocaleString("en-US", {
          minimumFractionDigits: 2,
        })
      : "",
    roiTestExpiresAt: campaign.roiTestConfig.expiresAt
      ? DateTime.fromMillis(campaign.roiTestConfig.expiresAt)
      : null,
    roiTestHoursStart:
      campaign.roiTestConfig.testHours?.start ??
      campaign.textingHoursStart ??
      subaccount.textingHoursStart,
    roiTestHoursEnd:
      campaign.roiTestConfig.testHours?.end ??
      campaign.textingHoursEnd ??
      subaccount.textingHoursEnd,
  };
}

export default function RoiSettingsForm({
  campaign,
  isSingleVariant = false,
  readOnly = false,
}: {
  campaign: CampaignForView;
  isSingleVariant?: boolean;
  readOnly?: boolean;
}): ReactElement {
  const { errors, values, setFieldValue } = useFormikContext<RoiFormValues>();
  const subaccount = useSubaccountAdminConfig()!;
  const timezone = DateTime.now().setZone(
    campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone
  ).offsetNameShort!;

  return (
    <>
      <FlexBox
        justifyContent="space-between"
        gap={3}
        sx={{ paddingBottom: theme.spacing(2) }}
      >
        <FormikTextField
          name="roiTestMinimumRoiPct"
          label="Minimum ROI"
          InputProps={{
            endAdornment: (
              <Typography variant="h1" sx={{ padding: theme.spacing(1) }}>
                %
              </Typography>
            ),
          }}
          sx={{ width: "50%" }}
          disabled={readOnly}
        />
        <FormikTextField
          name="roiTestMinimumDonationsPerVariant"
          label="Minimum Number of Donations (optional)"
          helperText={
            errors.roiTestMinimumDonationsPerVariant ??
            "Minimum donations to win"
          }
          sx={{ width: "50%" }}
          disabled={readOnly}
        />
      </FlexBox>
      <FormikTextField
        name="roiTestExcludeDonationsOver"
        label="Exclude Donations Over (optional)"
        helperText={
          errors.roiTestExcludeDonationsOver ??
          "Donations over this amount won't count towards the minimum ROI"
        }
        InputProps={{
          startAdornment: (
            <Typography variant="h1" sx={{ padding: theme.spacing(1) }}>
              $
            </Typography>
          ),
        }}
        fullWidth
        sx={{ paddingBottom: theme.spacing(2) }}
        disabled={readOnly}
      />
      <FormikDateTimePicker
        name="roiTestExpiresAt"
        label={`Expiration Date (${timezone}) (optional)`}
        fullWidth
        sx={{ paddingBottom: theme.spacing(2) }}
        disabled={readOnly}
      />
      <Typography variant="body2Bold">Autosend Window</Typography>
      <Typography variant="body2">
        {isSingleVariant
          ? `Messages will only be sent to the full list within these times.
             If the minimum ROI is reached after the autosend window, the full send won't
             go out until the start of the autosend window of the following day.`
          : `A winner will only be sent to the full list within these times. If a
          winner is reached after the autosend window, the full send won't go out
          until the start of the autosend window of the following day.`}
      </Typography>
      <FlexBox justifyContent="space-between" gap={3}>
        <FormikTextField
          type="time"
          name="roiTestHoursStart"
          label={`Start of Autosend Window (${timezone})`}
          value={timeToHHMM(values.roiTestHoursStart)}
          onChange={e => {
            const value = e.target.value || "";
            setFieldValue("roiTestHoursStart", timeFromHHMM(value)).catch(
              console.error
            );
          }}
          sx={{ width: "50%" }}
          disabled={readOnly}
        />
        <FormikTextField
          type="time"
          name="roiTestHoursEnd"
          label={`End of Autosend Window (${timezone})`}
          value={timeToHHMM(values.roiTestHoursEnd)}
          onChange={e => {
            const value = e.target.value || "";
            setFieldValue("roiTestHoursEnd", timeFromHHMM(value)).catch(
              console.error
            );
          }}
          sx={{ width: "50%" }}
          disabled={readOnly}
        />
      </FlexBox>
    </>
  );
}
